/**
 * Text-entry tick — ONE capture-phase `input` listener for the whole web app.
 *
 * `typing` is the most frequent sound in the product, so it is wired the
 * cheapest possible way: a single delegated listener installed by
 * `initWebSounds()`, rather than a prop threaded through every field. That
 * also means it covers everything that accepts typing — DS inputs, plain
 * fields, and the ProseMirror/tiptap contenteditable surfaces — with no
 * per-component opt-in to forget.
 *
 * The registry entry is `cooldownMs: 0`, so this is deliberately NOT
 * throttled: one tick per `input` event, tracking the user's keystrokes
 * exactly (see `shared/src/sound/cues.ts`).
 *
 * "Local text entry" is enforced two ways:
 *  - `event.isTrusted` — collaborative/remote edits and programmatic value
 *    writes never produce a trusted `input` event, so they stay silent.
 *  - a target allowlist — checkboxes, ranges, colour and file pickers all
 *    fire `input` too, and none of them are typing. Password fields are
 *    excluded on purpose: a per-keystroke tick would broadcast the length
 *    and rhythm of a secret to anyone in the room.
 */

import { playSound } from '@flashka/shared/sound';

const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'url', 'tel', 'number']);

function isTextEntry(target: EventTarget | null): boolean {
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(target.type);
  return target instanceof HTMLElement && target.isContentEditable;
}

let installed = false;

export function installTypingSound(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener(
    'input',
    (event) => {
      if (!event.isTrusted || !isTextEntry(event.target)) return;
      playSound('typing');
    },
    // Capture so a field that stops propagation can't mute the tick; passive
    // because the handler never touches the event.
    { capture: true, passive: true },
  );
}
